import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import Div from "@elements/div"
import { useSiteMetadata } from "@hooks/use-site-metadata"
import Flex from "@elements/flex"
import { ReactComponent as GoogleIcon } from "@images/google-login.svg"
import { isExternal } from "@utils/is-external"
import { Link } from "gatsby"
import theme from "@styles/theme"
import { PRODUCTS, useProduct } from "@hooks/use-product"
import { useCta } from "@hooks/use-cta"
import { cssConfig } from "@utils/css-validator"
import { isSameTab } from "@utils/is-same-tab"
import { useLocation } from "@hooks/use-location"

const StyledButton = styled(Div).withConfig(cssConfig)`
  :hover {
    filter: ${props => !props.disableBrightness && `brightness(90%)`};
    background: ${props => props.hoverBackground};
    color: ${props => props.hoverColor};
  }

  border: none;

  ${props =>
    props.notice &&
    css`
      &::before {
        text-align: center;
        display: block;
        content: ${props => `"${props.noticeText}"`};
        font-size: 9.5px;
        position: absolute;
        bottom: -1.5rem;
        left: 0;
        margin: auto;
        right: 0;
        color: ${props =>
          props.noticeColor || (props.text ? "#FFF" : "#21264f")};
        // Could use a function instead? Probably overkill
        // TODO: Deprecate props.text for props.noticeColour
        font-weight: bold;
        margin-top: 0.5rem;
      }
    `}

  ${props =>
    props.arrow &&
    css`
      &::after {
        content: "R";
        padding-left: 7px;
        display: inline-block;
        font-family: "klipfolio-icons";
        font-style: normal;
        font-weight: 400;
        line-height: 1;
        font-size: 0.8em;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        vertical-align: middle;
        transition: transform linear 0.2s;
      }

      &:hover {
        &::after {
          transform: translateX(6px);
        }
      }
    `}
`

StyledButton.defaultProps = {
  height: "min-content",
  display: "inline-block",
  position: "relative",
  padding: "0.5rem 2rem",
  flexFlow: "column",
  fontWeight: "700",
  color: "white",
  borderRadius: "2rem",
  transition: "filter .2s linear, background-color .2s linear",
  notice: true,
  cursor: "pointer",
}

StyledButton.propTypes = {
  arrow: PropTypes.bool,
  notice: PropTypes.bool,
  noticeText: PropTypes.string,
  text: PropTypes.bool,
  hoverBackground: PropTypes.string,
  hoverColor: PropTypes.string,
  noticeColor: PropTypes.string,
  disableBrightness: PropTypes.bool,
  ...Div.propTypes,
}

const Button = ({ children, noticeText, href, ...rest }) => {
  const external = isExternal(href)
  const defaultNotice = "Free for 14 days ● No credit card required"
  const sameTab = isSameTab(href)

  return href ? (
    external ? (
      <StyledButton
        noticeText={noticeText ?? defaultNotice}
        as="a"
        href={href}
        target={sameTab ? "_self" : "_blank"}
        {...rest}
      >
        {children}
      </StyledButton>
    ) : (
      <Link to={href}>
        <StyledButton
          noticeText={noticeText ?? defaultNotice}
          as="button"
          {...rest}
        >
          {children}
        </StyledButton>
      </Link>
    )
  ) : (
    <StyledButton
      noticeText={noticeText ?? defaultNotice}
      as="button"
      {...rest}
    >
      {children}
    </StyledButton>
  )
}

Button.propTypes = {
  ...StyledButton.propTypes,
  noticeText: PropTypes.string,
  children: PropTypes.node,
  href: PropTypes.string,
}

Button.Agnostic = ({ signup, children, ...rest }) => {
  const { ctaLink, cta } = useCta(signup, PRODUCTS.AGNOSTIC, null)
  return (
    <Button href={ctaLink} {...rest}>
      {children ?? cta}
    </Button>
  )
}

Button.Agnostic.defaultProps = {
  arrow: true,
  background: `linear-gradient(90deg, #DDF0FD -15.17%, #75E1B2 0.94%, #5DD5F6 51.08%, #5DD5F6 60.36%, #707EFA 107.37%, #FCE9E9 123.58%)`,
  color: theme.color.blue600,
  notice: true,
}

Button.Agnostic.propTypes = {
  ...Button.propTypes,
  signup: PropTypes.bool,
  children: PropTypes.node,
}

Button.Agnostic.displayName = "Agnostic CTA"

Button.PowerMetrics = ({ signup, children, ...rest }) => {
  const { ctaLink, cta } = useCta(signup, PRODUCTS.POWERMETRICS, null)
  return (
    <Button href={ctaLink} {...rest}>
      {children ?? cta}
    </Button>
  )
}

Button.PowerMetrics.defaultProps = {
  arrow: true,
  background: theme.gradient.powerMetricsButtonAlt,
  boxShadow:
    "0px 8px 16px rgba(75, 87, 197, 0.2), inset 0px -6px 14px rgba(255, 255, 255, 0.4)",
  color: theme.color.indigo600,
  notice: true,
  noticeColor: "white",
  noticeText: "Free forever ● No credit card required",
}

Button.PowerMetrics.propTypes = {
  ...Button.propTypes,
  force: PropTypes.bool,
  signup: PropTypes.bool,
  children: PropTypes.node,
}

Button.PowerMetrics.displayName = "PowerMetrics CTA"

Button.PowerMetricsSimple = ({ signup, children, force, ...rest }) => {
  const { ctaLink, cta } = useCta(signup, PRODUCTS.POWERMETRICS, null)
  return (
    <Button href={ctaLink} {...rest}>
      {children ?? cta}
    </Button>
  )
}

Button.PowerMetricsSimple.defaultProps = {
  arrow: true,
  background: theme.color.indigo600,
  color: "white",
  notice: true,
  noticeText: "Free forever ● No credit card required",
}

Button.PowerMetricsSimple.propTypes = {
  ...Button.propTypes,
  force: PropTypes.bool,
  signup: PropTypes.bool,
  children: PropTypes.node,
}

Button.PowerMetricsSimple.displayName = "PowerMetrics Simple CTA"

Button.Klips = ({ signup, children, ...rest }) => {
  const { ctaLink, cta } = useCta(signup, PRODUCTS.KLIPS, null)
  return (
    <Button href={ctaLink} {...rest}>
      {children ?? cta}
    </Button>
  )
}

Button.Klips.defaultProps = {
  background: `linear-gradient(90deg, #DDF0FD -15.17%, #75E1B2 0.94%, #5DD5F6 51.08%, #5DD5F6 60.36%, #707EFA 107.37%, #FCE9E9 123.58%)`,
  boxShadow:
    "0px 8px 16px rgba(24, 174, 216, 0.2), inset 0px 0 28px rgba(255, 255, 255, 0.3)",
  color: theme.color.blue600,
  arrow: true,
  klips: true,
  notice: true,
  noticeColor: theme.color.blue600,
  noticeText: "Free for 14 days ● No credit card required",
}

Button.Klips.propTypes = {
  ...Button.propTypes,
  force: PropTypes.bool,
  signup: PropTypes.bool,
  children: PropTypes.node,
}

Button.Klips.displayName = "Klips Button"

Button.KlipsSimple = ({ signup, children, ...rest }) => {
  const { ctaLink, cta } = useCta(signup, PRODUCTS.KLIPS, null)
  return (
    <Button href={ctaLink} {...rest}>
      {children ?? cta}
    </Button>
  )
}

Button.KlipsSimple.defaultProps = {
  background: theme.color.blue600,
  arrow: true,
  klips: true,
  notice: true,
  noticeText: "Free for 14 days ● No credit card required",
}

Button.KlipsSimple.propTypes = {
  ...Button.propTypes,
  force: PropTypes.bool,
  signup: PropTypes.bool,
  children: PropTypes.node,
}

Button.KlipsSimple.displayName = "Klips Simple Button"

Button.Google = ({ ...rest }) => {
  const { googleSignUp, siteUrl } = useSiteMetadata()
  const location = useLocation()

  const href =
    googleSignUp +
    `&start_on_metrics=false&force=true&page_source=${location?.pathname}&referrer=${siteUrl}${location.pathname}`

  return (
    <Button href={href} {...rest}>
      <Flex
        alignItems="center"
        flexFlow="row"
        justifyContent="center"
        gap="1rem"
      >
        <GoogleIcon />
        Sign up with Google
      </Flex>
    </Button>
  )
}

Button.Google.defaultProps = {
  background: "white",
  color: "black",
  notice: true,
}

Button.Google.propTypes = {
  klips: PropTypes.bool,
  ...Button.propTypes,
}

Button.Google.displayName = "Google Button"

export default Button
